/* eslint-disable @typescript-eslint/naming-convention */
import type { FC } from "react";
import { memo, useCallback, useEffect } from "react";

import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { Button, EmptyState } from "@similarweb/ui-components";

import { browserUtils } from "app/services/fetchService/browserUtils";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const StyledButton = styled(Button)`
    margin-top: 16px;
`;

const EmptyStateWrapper = styled.div`
    position: fixed;
    top: 45%;
    left: 53%;
`;

interface CtaButtonProps {
    resetErrorBoundary: () => void;
}

const CtaButton = memo(({ resetErrorBoundary }: CtaButtonProps) => {
    const onClick = () => {
        resetErrorBoundary();
        browserUtils.reloadPage();
    };

    return <StyledButton onClick={onClick}>{translate("error.boundary.cta.button.text")}</StyledButton>;
});

interface IErrorFallbackProps {
    resetErrorBoundary: () => void;
    error: Error;
    shouldLogErrorFallback?: boolean;
}

export const ErrorFallback: FC<IErrorFallbackProps> = ({
    resetErrorBoundary,
    error,
    shouldLogErrorFallback = true,
}) => {
    useEffect(() => {
        if (shouldLogErrorFallback) {
            const pageId = proNavigator.getCurrentPageId();
            Sentry.fatal(
                `Error boundary in: section: ${pageId?.section} - subSection: ${pageId?.subSection} - subSubSection: ${pageId?.subSubSection}`,
                error,
                {
                    tags: { ErrorType: "ErrorBoundary" },
                },
            );
        }
    }, [error, shouldLogErrorFallback]);

    const CtaButtonClb = useCallback(() => <CtaButton resetErrorBoundary={resetErrorBoundary} />, [resetErrorBoundary]);

    return (
        <EmptyStateWrapper>
            <EmptyState
                title={translate("error.boundary.title")}
                subtitle={translate("error.boundary.subtitle")}
                CtaButtons={CtaButtonClb}
                iconName={"no-data-tree"}
            />
        </EmptyStateWrapper>
    );
};
